// -----------------------------------------------------------------------------
// Right-hand pane of the chat page — channel header (title, members pill,
// mute toggle, member avatars) followed by the scrollable message list and
// the bottom composer. The thread auto-scrolls to the latest message
// whenever the active channel or message count changes.
// -----------------------------------------------------------------------------

import { useLayoutEffect, useRef } from "react";
import {
  BellAlertIcon,
  BellSlashIcon,
  HashtagIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline";

import classNames from "../../utils/classNames";
import { channelLabel } from "../../utils/chatHelpers";
import { IChatChannel, IChatMessage, IChatUser } from "../../models/Chat";
import ChatAvatar from "./ChatAvatar";
import Composer from "./Composer";
import MessageList from "./MessageList";

interface ChannelThreadProps {
  channel: IChatChannel;
  me: IChatUser;
  usersById: Map<string, IChatUser>;
  messages: IChatMessage[];
  loading: boolean;
  onSend: (body: string, replyToId?: string) => void;
  onEdit: (messageId: string, body: string) => void;
  onReact: (messageId: string, emoji: string) => void;
  onToggleMute: (channelId: string, muted: boolean) => void;
  onManageMembers: () => void;
}

export default function ChannelThread({
  channel,
  me,
  usersById,
  messages,
  loading,
  onSend,
  onEdit,
  onReact,
  onToggleMute,
  onManageMembers,
}: ChannelThreadProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [channel.id, messages.length]);

  const headerTitle = channelLabel(channel, me.id, usersById);

  const otherMembers = channel.memberIds
    .map((id) => usersById.get(id))
    .filter((u): u is IChatUser => !!u);

  return (
    <>
      <header className="flex items-center justify-between border-b border-gray-200 bg-white px-5 py-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            {channel.kind === "channel" ? (
              <HashtagIcon className="h-5 w-5 text-gray-400" />
            ) : null}
            <h2 className="truncate text-base font-semibold text-gray-900">
              {channel.kind === "channel" ? channel.name : headerTitle}
            </h2>
            {channel.kind === "channel" ? (
              <button
                type="button"
                onClick={onManageMembers}
                title="Manage members"
                className="group inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-gray-500 hover:bg-primary-50 hover:text-primary-700"
              >
                <span>{channel.memberIds.length} members</span>
                <PencilSquareIcon className="h-3 w-3 opacity-60 group-hover:opacity-100" />
              </button>
            ) : (
              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">
                Direct message
              </span>
            )}
          </div>
          {channel.description ? (
            <p className="truncate text-xs text-gray-500">
              {channel.description}
            </p>
          ) : channel.kind === "dm" ? (
            <p className="truncate text-xs text-gray-500">
              {otherMembers
                .filter((u) => u.id !== me.id)
                .map((u) => u.role || "Team")
                .join(" · ")}
            </p>
          ) : null}
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onToggleMute(channel.id, !channel.muted)}
            title={
              channel.muted
                ? "Unmute — count toward total again"
                : "Mute — keep badges but exclude from total"
            }
            className={classNames(
              "inline-flex h-8 w-8 items-center justify-center rounded-md border text-gray-500 hover:bg-gray-100",
              channel.muted
                ? "border-gray-200 bg-gray-100 text-gray-700"
                : "border-gray-200 bg-white"
            )}
          >
            {channel.muted ? (
              <BellSlashIcon className="h-4 w-4" />
            ) : (
              <BellAlertIcon className="h-4 w-4" />
            )}
          </button>
          <MemberAvatars members={otherMembers} max={5} />
        </div>
      </header>

      <div
        ref={scrollRef}
        className="flex-1 space-y-1 overflow-y-auto bg-gray-50/60 px-4 py-4"
      >
        {loading && messages.length === 0 ? (
          <div className="flex h-full items-center justify-center text-sm text-gray-400">
            Loading messages…
          </div>
        ) : (
          <MessageList
            messages={messages}
            usersById={usersById}
            meId={me.id}
            onEdit={onEdit}
            onReact={onReact}
          />
        )}
      </div>

      <Composer
        channel={channel}
        usersById={usersById}
        meId={me.id}
        onSend={onSend}
      />
    </>
  );
}

function MemberAvatars({
  members,
  max = 5,
}: {
  members: IChatUser[];
  max?: number;
}) {
  const shown = members.slice(0, max);
  const remaining = Math.max(0, members.length - shown.length);
  return (
    <div className="flex -space-x-2">
      {shown.map((m) => (
        <ChatAvatar
          key={m.id}
          user={m}
          size="xs"
          className="ring-2 ring-white"
        />
      ))}
      {remaining > 0 ? (
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-200 text-[10px] font-semibold text-gray-700 ring-2 ring-white">
          +{remaining}
        </span>
      ) : null}
    </div>
  );
}
